import React, { useEffect, useState } from 'react';
import { createGroup } from '../../services/groupService';
import { getPlans } from '../../services/planService';
import { getGroupSetupContent, updateGroupSetupContent } from '../../services/groupSetupContentService';
import { useAuth } from '../../context/AuthContext';

const GroupSetup = ({ onGroupCreated }) => {
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingPaused, setCheckingPaused] = useState(true);
  const [purchasesPaused, setPurchasesPaused] = useState(false);

  const [content, setContent] = useState({ title: '', warningText: '', descriptionText: '', badgeText: '' });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: '', warningText: '', descriptionText: '', badgeText: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchContent = async () => {
    try {
      const data = await getGroupSetupContent();
      setContent({
        title: data.title,
        warningText: data.warningText,
        descriptionText: data.descriptionText,
        badgeText: data.badgeText,
      });
    } catch (err) {
      // non-critical -- falls back to whatever's already in state (empty on first load)
    }
  };

  useEffect(() => {
    const checkPaused = async () => {
      try {
        const data = await getPlans();
        setPurchasesPaused(data.purchasesPaused);
      } catch (err) {
        // if this check fails, fail open -- let the actual create/checkout
        // calls enforce the real backend restriction either way
      } finally {
        setCheckingPaused(false);
      }
    };
    checkPaused();
    fetchContent();
  }, []);

  const startEdit = () => {
    setDraft(content);
    setSaveError('');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!draft.title.trim()) {
      setSaveError('Title is required');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await updateGroupSetupContent(draft);
      setContent(draft);
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a name for your cookbook');
      return;
    }

    setLoading(true);
    try {
      const data = await createGroup(name.trim());
      onGroupCreated(data.group);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create cookbook');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPaused) {
    return <p style={styles.status}>Loading...</p>;
  }

  if (purchasesPaused) {
    return (
      <div style={styles.card}>
        <h2 style={styles.title}>🔒 New Signups Temporarily Unavailable</h2>
        <p style={styles.text}>
          We're not accepting new cookbooks right now — please check back soon!
        </p>
        <p style={styles.hint}>
          Already have an invite? Check your email for the invite link — you
          don't need to create your own cookbook.
        </p>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={styles.card}>
        <h3 style={styles.editHeading}>Edit Page Content</h3>

        <label style={styles.fieldLabel}>Title</label>
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          style={styles.editInput}
        />

        <label style={styles.fieldLabel}>Warning callout</label>
        <textarea
          value={draft.warningText || ''}
          onChange={(e) => setDraft({ ...draft, warningText: e.target.value })}
          style={styles.editTextarea}
          rows={3}
        />

        <label style={styles.fieldLabel}>Description</label>
        <textarea
          value={draft.descriptionText || ''}
          onChange={(e) => setDraft({ ...draft, descriptionText: e.target.value })}
          style={styles.editTextarea}
          rows={2}
        />

        <label style={styles.fieldLabel}>Badge text</label>
        <input
          value={draft.badgeText || ''}
          onChange={(e) => setDraft({ ...draft, badgeText: e.target.value })}
          style={styles.editInput}
        />

        {saveError && <p style={styles.error}>{saveError}</p>}

        <div style={styles.editActions}>
          <button onClick={() => setEditing(false)} style={styles.cancelBtn} disabled={saving}>Cancel</button>
          <button onClick={handleSave} style={styles.button} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      {user?.is_admin && (
        <button onClick={startEdit} style={styles.editPageBtn}>✏️ Edit Page Content</button>
      )}

      <h2 style={styles.title}>{content.title}</h2>
      {content.warningText && (
        <p style={styles.inviteWarning}>{content.warningText}</p>
      )}
      {content.descriptionText && (
        <p style={styles.text}>{content.descriptionText}</p>
      )}
      {content.badgeText && (
        <p style={styles.oneTimeBadge}>{content.badgeText}</p>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          type="text"
          placeholder="e.g. The Varner Family Cookbook"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
          disabled={loading}
        />
        {error && <p style={styles.error}>{error}</p>}
        <button type="submit" style={styles.button} disabled={loading}>
          {loading ? 'Creating...' : 'Create Cookbook'}
        </button>
      </form>
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    marginBottom: '24px',
    maxWidth: '480px',
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
  },
  title: {
    color: '#e67e22',
    fontSize: '24px',
    marginBottom: '12px',
  },
  text: {
    color: '#666',
    fontSize: '15px',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  oneTimeBadge: {
    display: 'inline-block',
    backgroundColor: '#fdf0e3',
    color: '#a05a1a',
    fontSize: '13px',
    fontWeight: '600',
    padding: '8px 16px',
    borderRadius: '20px',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '15px',
  },
  error: {
    color: '#e74c3c',
    fontSize: '14px',
    margin: 0,
  },
  button: {
    padding: '12px 28px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  hint: {
    color: '#999',
    fontSize: '13px',
    marginTop: '20px',
  },
  inviteWarning: {
    color: '#a05a1a',
    backgroundColor: '#fdf0e3',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '20px',
    whiteSpace: 'pre-wrap',
  },
  status: {
    textAlign: 'center',
    color: '#999',
  },
  editPageBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    padding: '6px 12px',
    backgroundColor: '#fdf0e3',
    color: '#e67e22',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  editHeading: {
    color: '#e67e22',
    marginBottom: '16px',
    textAlign: 'left',
  },
  fieldLabel: {
    display: 'block',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#777',
    marginBottom: '4px',
    marginTop: '12px',
  },
  editInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  editTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
  },
  cancelBtn: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#666',
  },
};

export default GroupSetup;
